import { useEffect, useRef, useState } from "react";
import { useUISlice } from "@/models/useUISlice";
import { useAppStore } from "@/controller";
import { actions } from "@/controller";
import ModalContainer from "@/components/ModalContainer";
import { useTranslation } from "react-i18next";
import { useUpload } from "@/models/useUpload";
import { isAudioOrVideo, SUPPORTED_FILE_EXTENSIONS } from "@/file-types";
import { LANGUAGES, getDefaultTranscriptionLanguage } from "@/languages";
import { ChevronDownIcon } from "@/components/Icons";
import { useToast } from "@/models/useToast";
import useFileSelector from "@/models/useFileSelector";
import { getUserErrorMessage } from "@/client-error";
import { getImportFileLimit } from "@/file-picker";
import { getUploadAngle, isUploadEntry } from "@/onboarding-interactions";
import { logClientEvent } from "@/telemetry";
import { describeUpload, recordImportTiming } from "@/import-timing";
import { useRouter } from "@/_router";

const AlertWidgetCreateTranscriptFromFileImport = () => {
  const { t } = useTranslation();
  const { pathname } = useRouter();
  const { openFilesPicker } = useFileSelector();
  const isVisibleEvent = useUISlice(
    (state) => state.alertWidgetCreateTranscriptFromFileImport,
  );
  const transcriptionLanguage = useAppStore(
    (state) =>
      state.userData?.preferences?.transcriptionLanguage ||
      getDefaultTranscriptionLanguage(),
  );
  const isSubscribed = useAppStore((state) => Boolean(state.isSubscribed));
  const isLanguagePickerOpen = useUISlice((state) =>
    Boolean(state.alertLanguagePicker),
  );

  const [isProcessing, setIsProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const cancelledRef = useRef(false);
  const completedHandoffRef = useRef(false);
  const width = useUISlice((state) => state.device.width);
  const isMobile = useUISlice((state) => state.device.isMobile);
  const { isUploading, getUploadedFileUrl, cancelUpload, uploadProgress } =
    useUpload();
  const { toast } = useToast();
  const fromEntry = isUploadEntry(window.location.search);
  const angle = getUploadAngle(window.location.search);

  const showUploadError = (error: unknown) => {
    const message = getUserErrorMessage(error, {
      generic: t("alertCreateTranscriptFromFileImport.uploadError", {
        defaultValue: "Upload failed. Please try again.",
      }),
      networkUnavailable: t("alertLargeFileUpload.errorRetriesExhausted"),
      unsupportedFile: t("alertCreateTranscriptFromFileImport.onlyAudioAndVideoFilesSupported"),
    });
    if (message) toast.error(message);
  };

  const handleDismiss = () => {
    cancelledRef.current = true;
    cancelUpload();
    useUISlice
      .getState()
      .setAlertCreateWidgetTranscriptFromFileImport(undefined);
  };

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      cancelUpload({ silent: true });
    };
  }, [cancelUpload]);

  useEffect(() => {
    if (isVisibleEvent) return;
    setIsProcessing(false);
    setBatchProgress(null);
  }, [isVisibleEvent]);

  useEffect(() => {
    if (
      !completedHandoffRef.current ||
      pathname === "/home" ||
      !isVisibleEvent
    ) {
      return;
    }
    completedHandoffRef.current = false;
    useUISlice
      .getState()
      .setAlertCreateWidgetTranscriptFromFileImport(undefined);
  }, [isVisibleEvent, pathname]);

  if (!isVisibleEvent) return null;

  const isBusy = Boolean(isVisibleEvent.loading) || isUploading || isProcessing;
  // The prompt and the sheet's coach glow mount together so their shared
  // loop stays in step, including after the language picker closes.
  const showEntryPrompt =
    fromEntry &&
    !isUploading &&
    !isProcessing &&
    !isLanguagePickerOpen &&
    !isVisibleEvent.preselectedFile;

  const handleImport = async () => {
    let handedOff = false;
    // Funnel steps: opened → chosen (or closed empty) → each upload's outcome.
    const source = fromEntry ? "entry" : "home";
    try {
      // A file shared into the app via the OS skips the picker.
      let files;
      if (isVisibleEvent.preselectedFile) {
        files = [isVisibleEvent.preselectedFile];
      } else {
        logClientEvent("import.pickerOpened", { source });
        files = await openFilesPicker(
          SUPPORTED_FILE_EXTENSIONS,
          getImportFileLimit(isSubscribed),
        );
      }

      if (files.length === 0) {
        logClientEvent("import.pickerCancelled", { source });
        return;
      }
      logClientEvent("import.filesSelected", { source, count: files.length });
      const selectedAt = Date.now();

      cancelledRef.current = false;
      setIsProcessing(true);
      const transcriptIds: string[] = [];
      let failedFiles = 0;

      for (const [index, file] of files.entries()) {
        if (cancelledRef.current) break;
        setBatchProgress({ current: index + 1, total: files.length });

        try {
          const uploadStartedAt = Date.now();
          const transcriptId = await new Promise<string>((resolve, reject) => {
            void getUploadedFileUrl({
              file: file as File,
              onSuccess: async (downloadUrl) => {
                const uploadedAt = Date.now();
                const id = await isVisibleEvent.onImport({
                  file: file as File,
                  isAudioOrVideoFile: isAudioOrVideo(file.name),
                  languageCode: transcriptionLanguage,
                  downloadUrl,
                });
                // Logged once the transcript exists so its id joins this
                // upload to the server's logs for the same file.
                const details = describeUpload(
                  file,
                  (navigator as { connection?: { effectiveType?: string } })
                    .connection?.effectiveType,
                );
                logClientEvent("import.uploadCompleted", {
                  source,
                  transcriptId: id,
                  elapsedMs: uploadedAt - uploadStartedAt,
                  ...details,
                });
                // A single file lands straight on its transcript, so the wait
                // it reports there is the one the person actually sat through.
                if (files.length === 1) {
                  recordImportTiming(id, {
                    selectedAt,
                    uploadedAt,
                    sizeMb: details.sizeMb,
                  });
                }
                resolve(id);
              },
              onError: reject,
            }).catch(reject);
          });
          transcriptIds.push(transcriptId);
        } catch (error) {
          if (cancelledRef.current) break;
          failedFiles += 1;
          logClientEvent("import.uploadFailed", { source });
          showUploadError(error);
        }
      }

      if (!cancelledRef.current) {
        if (files.length > 1) {
          const result = `${t("alertCreateTranscriptFromFileImport.button")}: ${transcriptIds.length}/${files.length}`;
          if (failedFiles === 0) toast.success(result);
          else toast(result);
        }
        await isVisibleEvent.onImported({
          transcriptIds,
          totalFiles: files.length,
        });
        handedOff = files.length === 1 && transcriptIds.length === 1;
        completedHandoffRef.current = handedOff;
      }
    } catch (error) {
      console.error("Error selecting file:", error);
      showUploadError(error);
    } finally {
      if (!handedOff) {
        setIsProcessing(false);
        setBatchProgress(null);
      }
    }
  };

  // Phones float this over the empty top of the screen, above the bottom
  // sheet; desktops put it above the centered dialog, or beside it once the
  // window is wide enough (height is the scarce direction there). Only one
  // renders.
  const entryPrompt = (
    <>
      <div className="flex flex-col items-center text-center lg:items-start lg:text-start">
        <p className="entry-import-title text-2xl font-bold lg:text-4xl text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
          {t("alertCreateTranscriptFromFileImport.entryPromptTitle")}
        </p>
        <p className="entry-import-subtitle mt-1 text-base font-medium lg:mt-2 lg:text-lg text-white/90 drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]">
          {angle
            ? t(`alertCreateTranscriptFromFileImport.entryPromptAngle.${angle}`)
            : t("alertCreateTranscriptFromFileImport.entryPromptSubtitle")}
        </p>
        <p className="entry-import-trust mt-1.5 text-[13px] font-semibold text-white/85 drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]">
          {t("alertCreateTranscriptFromFileImport.entryPromptTrust")}
        </p>
        <ol className="entry-import-steps mt-4 flex flex-col items-center gap-2 lg:items-start">
          {[
            t("alertCreateTranscriptFromFileImport.entryPromptStepLanguage"),
            t("alertCreateTranscriptFromFileImport.entryPromptStepTranscribe", {
              action: t("alertCreateTranscriptFromFileImport.button"),
            }),
          ].map((step, index) => (
            <li
              key={index}
              className="entry-import-step glass-thin flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-3.5 text-sm font-semibold text-[var(--text-primary)]"
            >
              <span className="entry-import-step-number flex h-6 w-6 items-center justify-center rounded-full text-xs">
                {index + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
      </div>
      {/* A dot runs toward the dialog once per step: down on phones and
          narrow windows, sideways when the prompt sits beside it. */}
      <span
        aria-hidden="true"
        className="entry-import-runway mt-3 h-10 w-1 rounded-full"
      />
    </>
  );

  return (
    <>
      {showEntryPrompt && isMobile && (
        <div
          data-testid="entry-import-prompt"
          className="entry-import-prompt pointer-events-none fixed inset-x-4 top-[calc(env(safe-area-inset-top)+5vh)] z-[1001] flex flex-col items-center text-center"
        >
          {entryPrompt}
        </div>
      )}
      <ModalContainer
        title={t("alertCreateTranscriptFromFileImport.title")}
        subtitle={t(
          fromEntry
            ? "alertCreateTranscriptFromFileImport.entrySubtitle"
            : "alertCreateTranscriptFromFileImport.subtitle",
        )}
        onDismiss={handleDismiss}
        shouldPersistBackdropClick
        hideCancel
        hideDismissIcon={fromEntry}
        desktopHeader={
          showEntryPrompt ? (
            <div
              data-testid="entry-import-prompt"
              className="entry-import-prompt pointer-events-none mb-4 flex flex-col items-center lg:mb-0 lg:max-w-sm lg:flex-row lg:gap-8"
            >
              {entryPrompt}
            </div>
          ) : undefined
        }
        className={showEntryPrompt ? "entry-import-coach" : undefined}
        primaryButtonText={
          isVisibleEvent.loading
            ? t("home.loading")
            : isUploading || isProcessing
              ? `${t("alertCreateTranscriptFromFileImport.uploading", {
                  defaultValue: "Importing...",
                })} ${batchProgress ? `${batchProgress.current}/${batchProgress.total}` : ""} ${isUploading ? `${uploadProgress}%` : ""}`
              : t("alertCreateTranscriptFromFileImport.button")
        }
        onPrimaryButtonPress={handleImport}
        loading={isBusy}
        primaryButtonDisabled={isBusy}
        modalWidth={Math.min(width - 32, 440)}
        cancelText={t("modalContainer.cancelButton")}
      >
        <div className="flex flex-col gap-4">
          {isVisibleEvent.preselectedFile && (
            <div className="w-full bg-[var(--bg-secondary)] rounded-2xl px-5 py-3 shadow-sm">
              <p className="text-xs font-medium text-[var(--text-secondary)] mb-0.5">
                {t("alertCreateTranscriptFromFileImport.sharedFileLabel", {
                  defaultValue: "File to transcribe",
                })}
              </p>
              <p className="text-[15px] text-[var(--text-primary)] font-medium truncate">
                {isVisibleEvent.preselectedFile.name}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[var(--text-secondary)] ml-1 mb-2 block">
              {t("alertCreateTranscriptFromFileImport.languageLabel")}
            </label>
            <button
              data-action-id="import.language.open"
              onClick={() =>
                useUISlice.getState().setAlertLanguagePicker({
                  selectedLanguage: transcriptionLanguage,
                  onSelect: (languageCode) => {
                    void actions.user.changeTranscriptionLanguage(languageCode);
                  },
                })
              }
              disabled={isBusy}
              className="field field-emphasis w-full flex items-center justify-between rounded-2xl px-5 py-3 text-[var(--text-primary)] text-lg transition-all cursor-pointer active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              <span>
                {LANGUAGES.find((l) => l.value === transcriptionLanguage)
                  ?.label || transcriptionLanguage}
              </span>
              <ChevronDownIcon className="text-[var(--text-secondary)]" />
            </button>
          </div>
        </div>
      </ModalContainer>
    </>
  );
};

export default AlertWidgetCreateTranscriptFromFileImport;
